import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { PayPastDueRentPaymentPage } from './PayPastDueRentPaymentPage.component';
import { getRachelDashboard, requestInitiatePayment } from '@renter-hub/actions/requests';
import { closeNotification, showPageNotification } from '@common/actions/NotificationActions';
import { checkRequestActionSuccess, getSignInPage } from '@common/paymentsUtils/api';
import {
  DEFAULT_ERROR,
  PAYMENT_METHOD_INSUFFICIENT_FUNDS,
  PAYMENT_PAID,
  PAYMENT_UNPROCESSABLE,
} from '@renter-hub/constants/notifications';
import HTTP_STATUS from '@common/constants/HttpConstants';
import ResponseMessageCode from '@common/constants/ResponseMessageCode';
import { RequestInitiatePaymentInterface } from '@renter-hub/models/RequestsTypes';
import { RenterTransactionProps } from '@renter-hub/models/Types';
import TransactionConstants from '@common/constants/TransactionConstants';
import { ErrorInterface } from '@common/types';
import { logError } from '@zg-rentals/log-error';

const findRentPaymentTransaction = (scheduleMonth, transactions: RenterTransactionProps[]) => {
  return transactions
    .filter((transaction) => transaction.paymentCategory === TransactionConstants.CATEGORY.RENT_PAYMENT)
    .filter((transaction) => String(transaction.scheduleMonth) === scheduleMonth)[0];
};

const mapStateToProps = (state, { failedPaymentEmail, scheduleMonth }) => {
  const { transactions } = state.payments.renter.dashboard.paymentHistory;
  const decodedScheduleMonth = decodeURI(scheduleMonth);
  return {
    failedPaymentEmail,
    transaction: findRentPaymentTransaction(decodedScheduleMonth, transactions),
    today: new Date(),
  };
};

const mapDispatchToProps = (dispatch, { baseUrl, propertyId }: any) => ({
  onSubmit: async (rentPaymentInfo: RequestInitiatePaymentInterface) => {
    try {
      // Close any open banners
      await dispatch(closeNotification());

      // Request to create move-in costs payment
      const createRentPaymentResponse = await dispatch(requestInitiatePayment(rentPaymentInfo));
      await checkRequestActionSuccess(createRentPaymentResponse);

      // Request the latest renter status
      const [renterStatusResponse] = await dispatch(getRachelDashboard({ propertyId }));
      await checkRequestActionSuccess(renterStatusResponse);

      // Navigate to the dashboard
      await dispatch(push(baseUrl));

      // Show success notification
      await dispatch(showPageNotification(PAYMENT_PAID));

      return { error: false };
    } catch (err) {
      const error = err as ErrorInterface;
      if (error.action && error.action.payload) {
        const { status, response } = error.action.payload;
        const { messageCode } = response || {};

        // If 401 status, redirect to sign-in
        if (status === HTTP_STATUS.SC_UNAUTHORIZED) {
          window.location = getSignInPage();
          return null;
        }

        // If 422, show insufficient funds or card declined notification
        if (status === HTTP_STATUS.SC_UNPROCESSABLE_ENTITY) {
          logError({ error: err, context: { status, messageCode } });
          if (messageCode === ResponseMessageCode.INSUFFICIENT_FUNDS) {
            await dispatch(showPageNotification(PAYMENT_METHOD_INSUFFICIENT_FUNDS));
          } else {
            await dispatch(showPageNotification(PAYMENT_UNPROCESSABLE));
          }
          await dispatch(push(baseUrl));
          return { error: true };
        }
      }

      logError({ error: err });
      dispatch(showPageNotification(DEFAULT_ERROR));
      return { error: true };
    }
  },
  goToDashboard: async () => {
    await dispatch(closeNotification());
    await dispatch(push(baseUrl));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(PayPastDueRentPaymentPage);
